import { useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Upload, FileSpreadsheet, CheckCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import * as XLSX from "xlsx";

interface ParsedRecord {
  seller_id: string;
  prefix: string;
  county: string;
  origin_state: string;
  recorded_date: string | null;
  pid: string;
  first_name: string;
  last_name: string;
  suffix: string;
  business_name: string;
  street_address: string;
  city: string;
  state: string;
  zip_code: string;
  consideration: number | null;
  document_type: string;
  property_address: string;
  response: string;
  phone_number: string;
  email_address: string;
  household_income: string;
  date_of_birth: string;
  credit_card_user: string;
  tm_notes: string;
  tm_dropdown: string;
  mail_id: string;
  mail_date: string | null;
  mail_id2: string;
  mail_date2: string | null;
  // Melissa-corrected address fields (preferred over originals)
  md_address: string;
  md_city: string;
  md_state: string;
  md_zip: string;
  md_results: string;
}

interface MailingRecordUpsert {
  seller_id: string;
  county: string | null;
  origin_state: string | null;
  recorded_date: string | null;
  pid: string | null;
  first_name: string | null;
  last_name: string | null;
  suffix: string | null;
  business_name: string | null;
  street_address: string | null;
  city: string | null;
  state: string | null;
  zip_code: string | null;
  consideration: number | null;
  document_type: string | null;
  property_address: string | null;
  response: string | null;
  phone_number: string | null;
  email_address: string | null;
  household_income: string | null;
  date_of_birth: string | null;
  credit_card_user: string | null;
    prefix: string | null;
    tm_notes: string | null;
    tm_dropdown: string | null;
    mailing_status: string;
  times_mailed?: number;
  last_mailed_date?: string | null;
}

interface MailingHistoryInsert {
  mailing_record_id: string;
  campaign_name: string;
  mailed_date: string | null;
  mailing_round: number;
  letter_type: string;
}

const COLUMN_MAP: Record<string, string> = {
  sellerid: "seller_id",
  county: "county",
  originstate: "origin_state",
  recordeddate: "recorded_date",
  recordingdate: "recorded_date",
  filingdate: "recorded_date",
  pid: "pid",
  parcelid: "pid",
  prefix: "prefix",
  firstname: "first_name",
  lastname: "last_name",
  sellername: "seller_name",
  suffix: "suffix",
  businessname: "business_name",
  streetaddress: "street_address",
  city: "city",
  state: "state",
  zipcode: "zip_code",
  zip: "zip_code",
  consideration: "consideration",
  amount: "consideration",
  documenttype: "document_type",
  propertyaddress: "property_address",
  response: "response",
  phonenumber: "phone_number",
  mdphone: "phone_number",
  emailaddress: "email_address",
  mdemail: "email_address",
  householdincome: "household_income",
  mdhouseholdincome: "household_income",
  dateofbirth: "date_of_birth",
  mddateofbirth: "date_of_birth",
  dob: "date_of_birth",
  creditcarduser: "credit_card_user",
  mdcreditcarduser: "credit_card_user",
  // Melissa-corrected address fields
  mdaddress1: "md_address",
  mdaddress: "md_address",
  mdcity: "md_city",
  mdstate: "md_state",
  mdpostalcode: "md_zip",
  mdresults: "md_results",
  "tm notes": "tm_notes",
  tmnotes: "tm_notes",
  "tm dropdown": "tm_dropdown",
  tmdropdown: "tm_dropdown",
  "mail id": "mail_id",
  mailid: "mail_id",
  "mail date": "mail_date",
  maildate: "mail_date",
  "mail id2": "mail_id2",
  mailid2: "mail_id2",
  "mail date2": "mail_date2",
  maildate2: "mail_date2",
};

function parseExcelDate(val: any): string | null {
  if (!val) return null;
  if (typeof val === "number") {
    const d = XLSX.SSF.parse_date_code(val);
    if (d) return `${d.y}-${String(d.m).padStart(2, "0")}-${String(d.d).padStart(2, "0")}`;
  }

  const s = String(val).trim();
  if (!s) return null;

  const match = s.match(/^(\d{1,2})-([A-Za-z]{3})-(\d{2,4})$/);
  if (match) {
    const months: Record<string, string> = {
      jan: "01", feb: "02", mar: "03", apr: "04", may: "05", jun: "06",
      jul: "07", aug: "08", sep: "09", oct: "10", nov: "11", dec: "12",
    };
    const m = months[match[2].toLowerCase()];
    const y = match[3].length === 2 ? (parseInt(match[3], 10) > 50 ? `19${match[3]}` : `20${match[3]}`) : match[3];
    return m ? `${y}-${m}-${match[1].padStart(2, "0")}` : null;
  }

  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) return d.toISOString().split("T")[0];
  return null;
}

function parseConsideration(val: any): number | null {
  if (val == null) return null;
  const s = String(val).replace(/[$,\s]/g, "");
  const n = parseFloat(s);
  return Number.isNaN(n) ? null : n;
}

function inferLetterType(campaign: string): string {
  const lower = (campaign || "").toLowerCase();
  if (lower.includes("pc") || lower.includes("postcard") || lower.includes("post card")) return "postcard";
  if (lower.includes("insert")) return "insert";
  if (lower.includes("env") || lower.includes("envelope")) return "envelope";
  if (lower.includes("letter")) return "letter";
  return "other";
}

export function ExcelUpload({ onImportComplete }: { onImportComplete?: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [parsedData, setParsedData] = useState<ParsedRecord[]>([]);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [importResult, setImportResult] = useState<{ total: number; inserted: number; skipped: number } | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [defaultOriginState, setDefaultOriginState] = useState("");

  const handleFile = useCallback((f: File) => {
    setFile(f);
    setImportResult(null);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target?.result, { type: "array" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const raw: any[] = XLSX.utils.sheet_to_json(ws, { defval: "" });

        console.log("Raw headers from file:", Object.keys(raw[0] || {}));
        console.log("First raw row:", raw[0]);

        const records: ParsedRecord[] = raw.map((row, idx) => {
          const mapped: Record<string, any> = {};

          for (const key of Object.keys(row)) {
            const normalized = key.toLowerCase().replace(/[^a-z0-9 ]/g, "").trim();
            const dbCol = COLUMN_MAP[normalized];
            if (dbCol) mapped[dbCol] = row[key];
            // Also try without spaces
            if (!dbCol) {
              const noSpaces = normalized.replace(/\s+/g, "");
              const dbCol2 = COLUMN_MAP[noSpaces];
              if (dbCol2) mapped[dbCol2] = row[key];
            }
          }

          let firstName = String(mapped.first_name || "");
          let lastName = String(mapped.last_name || "");
          if (!firstName && !lastName && mapped.seller_name) {
            const fullName = String(mapped.seller_name).trim();
            const parts = fullName.split(/\s+/);
            if (parts.length >= 2) {
              lastName = parts[0];
              firstName = parts.slice(1).join(" ");
            } else {
              lastName = fullName;
            }
          }

          let sellerId = String(mapped.seller_id || "");
          if (!sellerId) {
            const nameKey = `${lastName}-${firstName}`.replace(/\s+/g, "").toUpperCase();
            const addrKey = String(mapped.street_address || "").replace(/\s+/g, "").substring(0, 20).toUpperCase();
            const county = String(mapped.county || "").toUpperCase();
            sellerId = `${county}-${nameKey}-${addrKey}`;
          }

          return {
            seller_id: sellerId,
            prefix: String(mapped.prefix || ""),
            county: String(mapped.county || ""),
            origin_state: String(mapped.origin_state || mapped.state || defaultOriginState || ""),
            recorded_date: parseExcelDate(mapped.recorded_date),
            pid: String(mapped.pid || ""),
            first_name: firstName,
            last_name: lastName,
            suffix: String(mapped.suffix || ""),
            business_name: String(mapped.business_name || ""),
            street_address: String(mapped.street_address || ""),
            city: String(mapped.city || ""),
            state: String(mapped.state || ""),
            zip_code: String(mapped.zip_code || ""),
            consideration: parseConsideration(mapped.consideration),
            document_type: String(mapped.document_type || ""),
            property_address: String(mapped.property_address || ""),
            response: String(mapped.response || ""),
            phone_number: String(mapped.phone_number || ""),
            email_address: String(mapped.email_address || ""),
            household_income: String(mapped.household_income || ""),
            date_of_birth: String(mapped.date_of_birth || ""),
            credit_card_user: String(mapped.credit_card_user || ""),
            tm_notes: String(mapped.tm_notes || ""),
            tm_dropdown: String(mapped.tm_dropdown || ""),
            mail_id: String(mapped.mail_id || ""),
            mail_date: parseExcelDate(mapped.mail_date),
            mail_id2: String(mapped.mail_id2 || ""),
            mail_date2: parseExcelDate(mapped.mail_date2),
            // Melissa-corrected address fields
            md_address: String(mapped.md_address || ""),
            md_city: String(mapped.md_city || ""),
            md_state: String(mapped.md_state || ""),
            md_zip: String(mapped.md_zip || ""),
            md_results: String(mapped.md_results || ""),
          };
        });

        console.log("First 3 mapped records:", records.slice(0, 3).map(r => ({ seller_id: r.seller_id, first_name: r.first_name, last_name: r.last_name, md_address: r.md_address })));
        
        // Check for seller_id uniqueness
        const uniqueIds = new Set(records.map(r => r.seller_id));
        console.log(`Unique seller_ids: ${uniqueIds.size} out of ${records.length} records`);
        
        setParsedData(records);
        toast.success(`Parsed ${records.length} records from file`);
      } catch (err) {
        toast.error("Failed to parse Excel file");
        console.error(err);
      }
    };

    reader.readAsArrayBuffer(f);
  }, [defaultOriginState]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  }, [handleFile]);

  const handleImport = async () => {
    if (!parsedData.length) return;

    setImporting(true);
    setProgress(0);
    let inserted = 0;
    const chunkSize = 500;

    // Deduplicate by seller_id — keep last occurrence (most complete data wins)
    const deduped = new Map<string, ParsedRecord>();
    for (const r of parsedData) {
      deduped.set(r.seller_id, r);
    }
    const uniqueRecords = Array.from(deduped.values());

    try {
      for (let i = 0; i < uniqueRecords.length; i += chunkSize) {
        const chunk = uniqueRecords.slice(i, i + chunkSize);

        const mailingRecords: MailingRecordUpsert[] = chunk.map((r) => {
          // Prefer Melissa-corrected address over original
          const finalAddress = r.md_address || r.street_address || null;
          const finalCity = r.md_city || r.city || null;
          const finalState = r.md_state || r.state || null;
          const finalZip = r.md_zip || r.zip_code || null;

          const rec: MailingRecordUpsert = {
            seller_id: r.seller_id,
            prefix: r.prefix || null,
            county: r.county || null,
            origin_state: r.origin_state || null,
            recorded_date: r.recorded_date,
            pid: r.pid || null,
            first_name: r.first_name || null,
            last_name: r.last_name || null,
            suffix: r.suffix || null,
            business_name: r.business_name || null,
            street_address: finalAddress,
            city: finalCity,
            state: finalState,
            zip_code: finalZip,
            consideration: r.consideration,
            document_type: r.document_type || null,
            property_address: r.property_address || null,
            response: r.response || null,
            phone_number: r.phone_number || null,
            email_address: r.email_address || null,
            household_income: r.household_income || null,
            date_of_birth: r.date_of_birth ? String(r.date_of_birth) : null,
            credit_card_user: r.credit_card_user ? String(r.credit_card_user) : null,
            tm_notes: r.tm_notes || null,
            tm_dropdown: r.tm_dropdown || null,
            mailing_status: r.response ? "responded" : "active",
          };

          const mailCount = (r.mail_id ? 1 : 0) + (r.mail_id2 ? 1 : 0);
          if (mailCount > 0) {
            rec.times_mailed = mailCount;
            rec.last_mailed_date = r.mail_date2 || r.mail_date || null;
          }

          return rec;
        });

        const { data: insertedRecords, error } = await supabase
          .from("mailing_records")
          .upsert(mailingRecords, { onConflict: "seller_id", ignoreDuplicates: false })
          .select("id, seller_id");

        if (error) {
          console.error("Insert error:", error);
          toast.error(`Error at row ${i}: ${error.message}`);
          return;
        }

        inserted += insertedRecords?.length || 0;

        if (insertedRecords?.length) {
          const sellerIdMap = new Map(insertedRecords.map((r: { seller_id: string; id: string }) => [r.seller_id, r.id]));
          const historyRows: MailingHistoryInsert[] = [];

          for (const r of chunk) {
            const recordId = sellerIdMap.get(r.seller_id);
            if (!recordId) continue;

            if (r.mail_id) {
              historyRows.push({
                mailing_record_id: recordId,
                campaign_name: r.mail_id,
                mailed_date: r.mail_date,
                mailing_round: 1,
                letter_type: inferLetterType(r.mail_id),
              });
            }

            if (r.mail_id2) {
              historyRows.push({
                mailing_record_id: recordId,
                campaign_name: r.mail_id2,
                mailed_date: r.mail_date2,
                mailing_round: 2,
                letter_type: inferLetterType(r.mail_id2),
              });
            }
          }

          if (historyRows.length > 0) {
            const recordIds = [...new Set(historyRows.map((h) => h.mailing_record_id))];
            await supabase.from("mailing_history").delete().in("mailing_record_id", recordIds);

            const { error: histError } = await supabase.from("mailing_history").insert(historyRows);
            if (histError) console.error("History insert error:", histError);
          }
        }

        setProgress(Math.round((Math.min(i + chunkSize, uniqueRecords.length) / uniqueRecords.length) * 100));
      }

      setImportResult({ total: uniqueRecords.length, inserted, skipped: Math.max(uniqueRecords.length - inserted, 0) });
      toast.success(`Import complete: ${inserted} records appended/updated by seller ID`);
      onImportComplete?.();
    } catch (err) {
      console.error(err);
      toast.error("Import failed");
    } finally {
      setImporting(false);
      setProgress(100);
    }
  };

  const preview = parsedData.slice(0, 50);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileSpreadsheet className="h-5 w-5" />
          Import Melissa Export
        </CardTitle>
        <CardDescription>
          Upload your Melissa-exported Excel or CSV file to append or update mailing records by seller ID.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!parsedData.length && (
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
              dragOver ? "border-primary bg-primary/5" : "border-muted-foreground/25 hover:border-primary/50"
            }`}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => {
              const input = document.createElement("input");
              input.type = "file";
              input.accept = ".xlsx,.xls,.csv";
              input.onchange = (e: Event) => {
                const target = e.target as HTMLInputElement;
                if (target.files?.[0]) handleFile(target.files[0]);
              };
              input.click();
            }}
          >
            <Upload className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
            <p className="text-sm font-medium">Drop your Melissa export here or click to browse</p>
            <p className="text-xs text-muted-foreground mt-1">Supports .xlsx, .xls, .csv</p>
          </div>
        )}

        {parsedData.length > 0 && !importResult && (
          <>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <p className="text-sm font-medium">
                {file?.name} — {parsedData.length.toLocaleString()} records found
              </p>
              <div className="flex items-center gap-2">
                <Label htmlFor="defaultOriginState" className="text-xs whitespace-nowrap">
                  Default Origin State:
                </Label>
                <Input
                  id="defaultOriginState"
                  value={defaultOriginState}
                  onChange={(e) => {
                    const val = e.target.value.toUpperCase().slice(0, 2);
                    setDefaultOriginState(val);
                    if (val) {
                      setParsedData((prev) =>
                        prev.map((r) => ({
                          ...r,
                          origin_state: r.origin_state || val,
                        })),
                      );
                    }
                  }}
                  placeholder="e.g. TX"
                  className="w-16 h-8 text-xs uppercase"
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setParsedData([]);
                  setFile(null);
                  setImportResult(null);
                }}
              >
                Clear
              </Button>
              <Button size="sm" onClick={handleImport} disabled={importing}>
                {importing ? "Importing..." : "Append / Update by Seller ID"}
              </Button>
            </div>

            {importing && <Progress value={progress} className="h-2" />}

            <div className="border rounded-md overflow-auto max-h-[400px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>SellerID</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Address (MD corrected)</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>DOB</TableHead>
                    <TableHead>Income</TableHead>
                    <TableHead>CC User</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {preview.map((r, i) => (
                    <TableRow key={i}>
                      <TableCell className="font-mono text-xs">{r.seller_id}</TableCell>
                      <TableCell className="whitespace-nowrap">{r.first_name} {r.last_name}</TableCell>
                      <TableCell className="text-xs whitespace-nowrap">
                        {r.md_address || r.street_address || "—"}, {r.md_city || r.city || ""} {r.md_state || r.state || ""} {r.md_zip || r.zip_code || ""}
                      </TableCell>
                      <TableCell className="text-xs">{r.phone_number || "—"}</TableCell>
                      <TableCell className="text-xs">{r.email_address || "—"}</TableCell>
                      <TableCell className="text-xs">{r.date_of_birth || "—"}</TableCell>
                      <TableCell className="text-xs">{r.household_income || "—"}</TableCell>
                      <TableCell className="text-xs">{r.credit_card_user || "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {parsedData.length > 50 && (
              <p className="text-xs text-muted-foreground text-center">
                Showing first 50 of {parsedData.length.toLocaleString()} records
              </p>
            )}
          </>
        )}

        {importResult && (
          <div className="flex items-center gap-3 p-4 bg-muted rounded-lg border">
            <CheckCircle className="h-5 w-5 text-primary" />
            <div>
              <p className="font-medium">Import Complete</p>
              <p className="text-sm text-muted-foreground">
                {importResult.inserted.toLocaleString()} records appended or updated successfully
                {importResult.skipped > 0 && ` (${importResult.skipped} not returned by upsert)`}
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="ml-auto"
              onClick={() => {
                setParsedData([]);
                setFile(null);
                setImportResult(null);
              }}
            >
              Import Another
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
